/**
 * Progress Panel - Task Status Tracking Component
 *
 * Displays:
 * - Overall progress percentage
 * - Task status breakdown (not started, in progress, completed)
 * - Phase progress bars
 * - Recent completions
 *
 * Design: Compact sidebar panel with visual progress indicators
 *
 * All figures go through `actionPlanValueFormat` (P0-4). This panel carried its
 * own copy of `formatCurrency` that returned '$0' for absent input, and computed
 * three progress-bar widths by unguarded division and interpolation, so a plan
 * with no total value produced `width: NaN%`.
 */

import React from 'react';
import type { ActionPlanSummary, Phase } from '../types/actionPlan';
import { hasValue } from '../types/actionPlanValueProvenance';
import {
  barWidthPct,
  finiteOrNull,
  formatCurrency,
  formatPercent,
  formatWeeks,
} from './actionPlanValueFormat';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ProgressPanelProps {
  summary: ActionPlanSummary;
  phases: Phase[];
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProgressPanel: React.FC<ProgressPanelProps> = ({
  summary,
  phases
}) => {
  // A remainder is only meaningful when both sides of the subtraction exist.
  // `total - undefined` is NaN, which the old formatter then printed as '$0'.
  // `total_value_usd` is Quantified: unavailable whenever ANY task lacks a value.
  const totalValue = hasValue(summary?.total_value_usd)
    ? finiteOrNull(summary.total_value_usd.value)
    : null;
  // "Forecast", not "realized" (P1-7). Nothing in the product measures value
  // realization: this field was hardcoded to 0 on every server write path, so the
  // old heading described a measurement that was never taken.
  const forecastValue = finiteOrNull(summary?.value_forecast_usd);
  const remainingValue =
    totalValue === null || forecastValue === null ? null : totalValue - forecastValue;

  return (
    <div className="progress-panel">
      <h3>Progress Tracking</h3>

      {/* Overall Progress */}
      <div className="overall-progress">
        <div className="progress-label">
          <span>Overall Completion</span>
          <span className="progress-percentage">
            {formatPercent(summary?.overall_progress_pct)}
          </span>
        </div>
        <div className="progress-bar-large">
          <div
            className="progress-fill"
            style={{ width: barWidthPct(summary?.overall_progress_pct, 100) }}
          ></div>
        </div>
        <div className="progress-details">
          <span className="completed">{summary.tasks_completed} completed</span>
          <span className="in-progress">{summary.tasks_in_progress} in progress</span>
          <span className="not-started">{summary.tasks_not_started} not started</span>
        </div>
      </div>

      {/* Task Status Breakdown */}
      <div className="status-breakdown">
        <h4>Task Status</h4>
        <div className="status-grid">
          <div className="status-item completed">
            <div className="status-icon">✅</div>
            <div className="status-content">
              <div className="status-count">{summary.tasks_completed}</div>
              <div className="status-label">Completed</div>
            </div>
          </div>

          <div className="status-item in-progress">
            <div className="status-icon">🔄</div>
            <div className="status-content">
              <div className="status-count">{summary.tasks_in_progress}</div>
              <div className="status-label">In Progress</div>
            </div>
          </div>

          <div className="status-item not-started">
            <div className="status-icon">⏸️</div>
            <div className="status-content">
              <div className="status-count">{summary.tasks_not_started}</div>
              <div className="status-label">Not Started</div>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="phase-progress">
        <h4>Progress by Phase</h4>
        <div className="phase-list">
          {phases.map(phase => (
            <div key={phase.id} className="phase-item">
              <div className="phase-header">
                <span className="phase-name">{phase.name}</span>
                <span className="phase-percentage">{formatPercent(phase.progress_pct)}</span>
              </div>
              <div className="phase-progress-bar">
                <div
                  className={`phase-fill phase-${phase.id}`}
                  style={{ width: barWidthPct(phase.progress_pct, 100) }}
                ></div>
              </div>
              <div className="phase-meta">
                <span className="phase-tasks">{phase.task_ids.length} tasks</span>
                <span className="phase-value">{formatCurrency(phase.total_value_usd)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Value Progress */}
      <div className="value-progress">
        <h4>Forecast Value</h4>
        <div className="value-stats">
          <div className="value-stat">
            <div className="value-label">Forecast</div>
            <div className="value-amount realized">
              {formatCurrency(forecastValue)}
            </div>
          </div>
          <div className="value-stat">
            <div className="value-label">Remaining</div>
            <div className="value-amount remaining">
              {formatCurrency(remainingValue)}
            </div>
          </div>
          <div className="value-stat">
            <div className="value-label">Total</div>
            <div className="value-amount total">
              {formatCurrency(totalValue)}
            </div>
          </div>
        </div>
        <div className="value-bar">
          <div
            className="value-fill"
            style={{
              width: barWidthPct(summary?.value_forecast_usd, totalValue)
            }}
          ></div>
        </div>
      </div>

      {/* Critical Path Status */}
      <div className="critical-path-status">
        <h4>Critical Path</h4>
        <div className="critical-stats">
          <div className="critical-stat">
            <span className="stat-icon">📍</span>
            <div className="stat-content">
              {/* Interpolating a null renders "nullw" (S4d). */}
              <div className="stat-value">{formatWeeks(summary?.weeks_remaining)}</div>
              <div className="stat-label">Remaining</div>
            </div>
          </div>
          <div className="critical-stat">
            <span className="stat-icon">⏱️</span>
            <div className="stat-content">
              <div className="stat-value">{summary.critical_path_length || 0}</div>
              <div className="stat-label">Critical Tasks</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
